import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { OAUTH_AUTO_REDIRECT, OAUTH_REDIRECT_KEY, TERMS_VERSION } from '../../contexts/authConstants';
import TermsConsentModal from '../../components/feature/TermsConsentModal';
import SupabaseUserService from '../../services/supabaseUserService';
import { logger } from '../../utils/logger';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [redirectAfterConsent, setRedirectAfterConsent] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const userService = SupabaseUserService.getInstance();

  const getConsentStorageKey = (uid: string) => `magistral.terms.acceptedVersion.${uid}`;
  const processedRef = useRef(false)

  const decodeJwtSub = (token?: string | null): string | null => {
    try {
      if (!token) return null
      const [_h, p] = token.split('.')
      const json = JSON.parse(atob(p))
      return json?.sub || null
    } catch { return null }
  }

  const restFetch = async (path: string, accessToken: string, options?: RequestInit) => {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/${path}`
    const headers: Record<string, string> = {
      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${accessToken}`,
      ...(options?.headers as any || {})
    }
    const resp = await fetch(url, { ...options, headers })
    return resp
  }

  useEffect(() => {
    const finishAuth = async () => {
      try {
        logger.debug('[AuthCallback] Processando retorno OAuth...');
        logger.debug('[AuthCallback] URL atual:', window.location.href);
        logger.debug('[AuthCallback] Hash:', window.location.hash);
        logger.debug('[AuthCallback] Query:', window.location.search);
        
        // Extrair tokens do hash fragment (fluxo implicit)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const hashAccessToken = hashParams.get('access_token');
        const hashRefreshToken = hashParams.get('refresh_token');
        const hashExpiresAt = hashParams.get('expires_at');
        const hashExpiresIn = hashParams.get('expires_in');
        // Alguns navegadores/dispositivos podem retornar tokens na query string (fallback)
        const queryParams = new URLSearchParams(window.location.search);
        const queryAccessToken = queryParams.get('access_token');
        const queryRefreshToken = queryParams.get('refresh_token');
        const queryExpiresAt = queryParams.get('expires_at');
        const queryExpiresIn = queryParams.get('expires_in');
        const codeParam = queryParams.get('code');
        const typeParam = queryParams.get('type');
        const emailParam = queryParams.get('email');
        const tokenHashParam = queryParams.get('token_hash');
        const tokenParam = queryParams.get('token');
        // Unificar origem dos tokens (hash preferencial; fallback query)
        const accessToken = hashAccessToken || queryAccessToken;
        const refreshToken = hashRefreshToken || queryRefreshToken;
        const expiresAt = hashExpiresAt || queryExpiresAt;
        const expiresIn = hashExpiresIn || queryExpiresIn;
        const errorDesc = queryParams.get('error_description');
        if (errorDesc) {
          logger.warn('[AuthCallback] error_description recebido:', errorDesc);
        }
        
        logger.sensitive('[AuthCallback] Tokens encontrados:', {
          accessToken: !!accessToken,
          refreshToken: !!refreshToken,
          expiresAt,
          expiresIn
        });
        
        // Persistir tokens imediatamente para consumo REST, mesmo antes do SDK
        if (accessToken && refreshToken) {
          try {
            localStorage.setItem('supabase.auth.token', JSON.stringify({
              access_token: accessToken,
              refresh_token: refreshToken,
              expires_at: expiresAt ? Number(expiresAt) : undefined,
              expires_in: expiresIn ? Number(expiresIn) : undefined,
              token_type: 'bearer'
            }))
            logger.debug('[AuthCallback] Tokens salvos no localStorage (pré-setSession)')
          } catch (e) {
            logger.warn('[AuthCallback] Falha ao salvar tokens pré-setSession:', e)
          }

          // Definir sessão de forma não-bloqueante e com timeout de segurança
          try {
            logger.debug('[AuthCallback] Definindo sessão manualmente (async)...');
            void supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
              .then(({ data, error }) => {
                if (error) {
                  logger.warn('[AuthCallback] setSession retornou erro (async):', error)
                  return
                }
                logger.sensitive('[AuthCallback] Sessão definida (async):', data?.session?.user?.id)
              })
              .catch((e) => logger.warn('[AuthCallback] setSession falhou (async):', e))
          } catch (e) {
            logger.warn('[AuthCallback] Não foi possível iniciar setSession:', e)
          }
        } else if (codeParam) {
          // Fallback PKCE: se a URL vier com ?code=..., tentar trocar por sessão
          try {
            logger.debug('[AuthCallback] Trocando authorization code por sessão (PKCE)...');
            const { data: exData, error: exchangeError } = await (supabase.auth as any).exchangeCodeForSession(codeParam);
            if (exchangeError) {
              logger.error('[AuthCallback] Falha no exchangeCodeForSession:', exchangeError);
              // continua fluxo para tratar abaixo
            } else {
              logger.sensitive('[AuthCallback] Sessão obtida via code:', exData?.session?.user?.id);
            }
          } catch (ex) {
            logger.error('[AuthCallback] Exceção no exchangeCodeForSession:', ex);
          }
        } else if (typeParam && (tokenHashParam || (tokenParam && emailParam))) {
          // Fluxos vindos de email (signup/magiclink/recovery/email_change/invite) — comum no iOS
          try {
            logger.debug('[AuthCallback] verifyOtp iniciado (email flow)...', {
              typeParam,
              hasTokenHash: !!tokenHashParam,
              hasToken: !!tokenParam,
              hasEmail: !!emailParam
            });
            if (tokenHashParam) {
              const { data, error } = await (supabase.auth as any).verifyOtp({ type: typeParam as any, token_hash: tokenHashParam });
              if (error) {
                logger.error('[AuthCallback] verifyOtp (token_hash) falhou:', error);
              } else {
                logger.sensitive('[AuthCallback] verifyOtp (token_hash) OK, user:', data?.user?.id);
              }
            } else if (tokenParam && emailParam) {
              const { data, error } = await (supabase.auth as any).verifyOtp({ type: typeParam as any, email: emailParam, token: tokenParam });
              if (error) {
                logger.error('[AuthCallback] verifyOtp (token/email) falhou:', error);
              } else {
                logger.sensitive('[AuthCallback] verifyOtp (token/email) OK, user:', data?.user?.id);
              }
            }
          } catch (ex) {
            logger.error('[AuthCallback] Exceção no verifyOtp:', ex);
          }
        } else {
          logger.warn('[AuthCallback] Tokens não encontrados no hash e sem authorization code');
        }
        
        // Tentar obter sessão (pequeno retry), ou decodificar userId do token
        let session = null as Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session'] | null
        let tries = 0
        while (tries < 5) {
          const { data } = await supabase.auth.getSession()
          session = data.session
          if (session?.user) break
          await new Promise(r => setTimeout(r, 150))
          tries++
        }
        const rawAccess = accessToken || session?.access_token || JSON.parse(localStorage.getItem('supabase.auth.token') || '{}')?.access_token
        const userIdFromToken = decodeJwtSub(rawAccess)
        const effectiveUserId = session?.user?.id || userIdFromToken
        if (!effectiveUserId || !rawAccess) {
          logger.error('[AuthCallback] Não foi possível determinar o usuário ou token');
          setError('Não foi possível concluir a autenticação.');
          setTimeout(() => navigate('/login', { replace: true }), 2000);
          return
        }
        logger.sensitive('[AuthCallback] Usuário identificado:', effectiveUserId)
        setUserId(effectiveUserId)

        const storedRedirect = localStorage.getItem(OAUTH_REDIRECT_KEY);
        localStorage.removeItem(OAUTH_REDIRECT_KEY);

        let redirectPath = '/dashboard';
        const isEmailFlow = typeParam === 'signup' || typeParam === 'invite' || typeParam === 'email_change' || typeParam === 'magiclink' || typeParam === 'recovery';

        if (storedRedirect && storedRedirect !== OAUTH_AUTO_REDIRECT) {
          redirectPath = storedRedirect;
          logger.success('[AuthCallback] Usando redirect armazenado:', redirectPath);
        } else if (isEmailFlow) {
          redirectPath = '/signup/confirmed';
          logger.success('[AuthCallback] Fluxo de confirmação detectado. Redirecionando para:', redirectPath);
        } else {
          logger.debug('[AuthCallback] Verificando família do usuário (REST)...');
          try {
            const resp = await restFetch(`families?created_by=eq.${effectiveUserId}&select=id`, rawAccess)
            if (resp.status === 404 || resp.status === 406) {
              redirectPath = '/setup-family'
            } else if (!resp.ok) {
              throw new Error(`families REST status ${resp.status}`)
            } else {
              const body = await resp.json()
              const fam = Array.isArray(body) ? body[0] : body
              redirectPath = fam ? '/dashboard' : '/setup-family'
            }
            logger.success('[AuthCallback] Redirecionando para:', redirectPath);
          } catch (familyError) {
            logger.error('[AuthCallback] Erro ao verificar família (REST):', familyError);
            redirectPath = '/setup-family';
          }
        }

        // Garantir tokens persistidos (idempotente)
        try {
          const curr = JSON.parse(localStorage.getItem('supabase.auth.token') || '{}')
          const toWrite = {
            access_token: curr.access_token || rawAccess,
            refresh_token: curr.refresh_token || refreshToken,
            expires_at: curr.expires_at || (expiresAt ? Number(expiresAt) : undefined),
            expires_in: curr.expires_in || (expiresIn ? Number(expiresIn) : undefined),
            token_type: curr.token_type || 'bearer'
          }
          localStorage.setItem('supabase.auth.token', JSON.stringify(toWrite));
          logger.debug('[AuthCallback] Tokens garantidos no localStorage');
        } catch (storageError) {
          logger.warn('[AuthCallback] Não foi possível persistir tokens:', storageError);
        }

        // IMPORTANTE: Páginas de confirmação de email (/signup/confirmed, /instagram/confirmed)
        // não devem verificar consentimento aqui. São páginas informativas que apenas mostram
        // sucesso da confirmação. O consentimento será verificado posteriormente pelo
        // TermsConsentGate quando o usuário tentar acessar o app via PWA.
        const isEmailConfirmationPage = redirectPath === '/signup/confirmed' || redirectPath === '/instagram/confirmed';
        
        if (isEmailConfirmationPage) {
          logger.debug('[AuthCallback] Redirecionamento para página de confirmação de email. Pulando verificação de consentimento.');
          // Limpar hash/query para evitar reprocessamento
          try {
            window.history.replaceState({}, document.title, window.location.pathname)
          } catch {
            // Ignore history manipulation errors
          }
          navigate(redirectPath, { replace: true });
          return;
        }

        // Short-circuit: se já temos aceite local (por usuário) para a versão atual, não abrir modal
        try {
          if (session?.user?.id) {
            const storageKey = getConsentStorageKey(session.user.id);
            const localAccepted = localStorage.getItem(storageKey);
            if (localAccepted === TERMS_VERSION) {
              // Limpa chave legada (sem userId) se ainda existir
              localStorage.removeItem('magistral.terms.acceptedVersion');
              logger.debug('[AuthCallback] Consentimento local (por usuário) já registrado para a versão atual. Pulando verificação remota.');
              navigate(redirectPath, { replace: true });
              return;
            }
            // Caso exista a chave antiga, remover para evitar falsos positivos em outros usuários
            const legacy = localStorage.getItem('magistral.terms.acceptedVersion');
            if (legacy) {
              localStorage.removeItem('magistral.terms.acceptedVersion');
            }
          }
        } catch {
          /* ignore localStorage errors (private mode, quota, etc.) */
        }

        // Verificar consentimento para a versão atual dos termos
        try {
          const resp = await restFetch(`user_consents?user_id=eq.${effectiveUserId}&version=eq.${TERMS_VERSION}&select=id&limit=1`, rawAccess, {
            headers: { 'Accept': 'application/vnd.pgrst.object+json' }
          })
          if (resp.status === 404 || resp.status === 406) {
            logger.debug('[AuthCallback] Consentimento ausente para a versão atual. Abrindo modal...');
            setRedirectAfterConsent(redirectPath);
            setShowConsentModal(true);
            return; // aguardar interação do usuário
          } else if (!resp.ok) {
            logger.warn('[AuthCallback] Erro ao verificar consentimento (REST):', resp.status)
          }
        } catch (cErr) {
          logger.warn('[AuthCallback] Exceção na verificação de consentimento:', cErr);
        }

        // Limpar hash/query para evitar reprocessamento e navegação em loop
        try {
          window.history.replaceState({}, document.title, window.location.pathname)
        } catch {
          // Ignore history manipulation errors
        }
        navigate(redirectPath, { replace: true });
      } catch (err) {
        logger.error('[AuthCallback] Erro ao concluir OAuth:', err);
        setError('Não foi possível concluir a autenticação.');
        setTimeout(() => navigate('/login', { replace: true }), 2000);
      }
    };

    if (processedRef.current) return
    processedRef.current = true
    void finishAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#74935C] flex flex-col items-center justify-center px-6 py-8">
      <div className="w-full max-w-sm bg-white rounded-3xl p-8 shadow-card border border-gray-100 text-center">
        <img src="/Imagens/logo-loading-callback.gif" alt="Carregando" className="w-16 h-16 mx-auto mb-4 object-contain" />
        <h1 className="text-xl font-semibold text-gray-800 mb-2">Conectando à sua conta...</h1>
        <p className="text-sm text-gray-500">
          Redirecionando de forma segura. Caso nada aconteça, volte para a tela de login.
        </p>
        {error && (
          <p className="text-sm text-red-600 mt-4">
            {error}
          </p>
        )}
      </div>

      <TermsConsentModal
        isOpen={showConsentModal}
        isSubmitting={isSubmitting}
        termsVersion={TERMS_VERSION}
        onDecline={async () => {
          try {
            await supabase.auth.signOut();
          } finally {
            setShowConsentModal(false);
            navigate('/signup', { replace: true });
          }
        }}
        onAccept={async () => {
          if (!userId) return;
          setIsSubmitting(true);
          try {
            let ip: string | undefined;
            try {
              const resp = await fetch('https://api.ipify.org?format=json');
              const json = await resp.json();
              ip = json?.ip;
            } catch {
              // silencioso se falhar
            }

            await userService.registerConsent({
              userId,
              termsAccepted: true,
              privacyPolicyAccepted: true,
              version: TERMS_VERSION,
              ipAddress: ip,
              userAgent: navigator.userAgent
            });

            // Cache local por usuário: marcar versão aceita e limpar chave legada
            try {
              const storageKey = getConsentStorageKey(userId);
              localStorage.setItem(storageKey, TERMS_VERSION);
              localStorage.removeItem('magistral.terms.acceptedVersion');
            } catch {
              /* ignore localStorage errors (private mode, quota, etc.) */
            }

            navigate(redirectAfterConsent || '/dashboard', { replace: true });
          } catch (e) {
            logger.error('[AuthCallback] Falha ao registrar consentimento:', e);
            setError('Não foi possível registrar o consentimento. Tente novamente.');
            setIsSubmitting(false);
          }
        }}
      />
    </div>
  );
}
